/**
 * Performance Context interface
 * @property {string} transactionId - The unique identifier (GUID) for the performance transaction, used for correlating related performance metrics and logs.
 * @property {number|null} herdId - The identifier of the herd associated with the performance transaction, if applicable. This can be null if the transaction is not related to a specific herd.
 */
export interface PerformanceContext {
    transactionId: string;
    herdId: number | null;
}

/**
 * Herd interface
 * @property {number} herdId - The unique identifier for the herd.
 * @property {number} farmId - The unique identifier for the farm associated with the herd.
 * @property {string} name - The name of the herd.
 * @property {string} description - A description of the herd.
 * @property {string} herdKey - The key associated with the herd.
 * @property {string} herdMgmtSoftware - The software used for herd management.
 * @property {string} herdSoftwareTypeId - The type of software used for herd management.
 * @property {string} latestCowEventDate - The date of the latest cow event.
 * Properties in the table , but not returned to the frontend:
 * @property {number} bornCalfPrice - The price of a born calf.
 * @property {number} cullCowPrice - The price for culling a cow.
 * @property {number} drugCostPerCow - The cost of drugs per cow.
 * @property {number} heatDetectionCostPerCow - The cost of heat detection per cow.
 * @property {number} heiferReplacementValuePerCow - The replacement value of a heifer per cow.
 * @property {number} feedCostPerPoundOfDryMatterPerCow - The cost of feed per pound of dry matter per cow.
 * @property {number} laborCostPerCow - The labor cost per cow.
 * @property {number} milkPricePerCwt - The price of milk per hundredweight (cwt).
 * @property {number} OtherRevenue - Other revenue associated with the herd.
 * @property {number} premiumMilkPerPound - The premium price for milk per pound.
 * @property {number} semenCostPerCow - The cost of semen per cow.
 * @property {number} breedingCost - The cost of breeding.
 * @property {number} breedingCostUnitTypeId - The unit type ID for breeding cost.
 * @property {number} gnRHCostPerDose - The cost of GnRH per dose.
 * @property {number} GnRHProductTypeId - The product type ID for GnRH.
 * @property {number} pgCostPerDose - The cost of PG per dose.
 * @property {number} pgProductTypeId - The product type ID for PG.
 * @property {number} professionalServiceCostPerCow - The cost of professional services per cow.
 * @property {number} femaleCalfPrice - The cost of a femaile calf.
 * @property {number} cIDRCostPerDose - The cost of CIDR per dose.
 * @property {number} feedCostDryPerPound - The cost of dry feed per pound.
 * @property {number} feedCostLactatingPerPound - The cost of lactating feed per pound.
 * @property {boolean} useLegacyKpiProcessing - Flag indicating whether to use legacy KPI processing.
 * @property {boolean} useLegacyFertilityProcessing - Flag indicating whether to use legacy fertility processing.
 * @property {string} latestCowfileSizeJson - The size of the latest cow file in JSON format.
 * @property {number} longitude - The longitude of the herd's location.
 * @property {number} latitude - The latitude of the herd's location.
 */
export interface Herd {
    herdId: number;
    farmId: number;
    name: string;
    description: string;
    herdKey: string;
    herdMgmtSoftware: string;
    herdSoftwareTypeId: number;
    // bornCalfPrice: number;
    // cullCowPrice: number;
    // drugCostPerCow: number;
    // heatDetectionCostPerCow: number;
    // heiferReplacementValuePerCow: number;
    // feedCostPerPoundOfDryMatterPerCow: number;
    // laborCostPerCow: number;
    // milkPricePerCwt: number;
    // OtherRevenue: number;
    // premiumMilkPerPound: number;
    // semenCostPerCow: number;
    // breedingCost: number;
    // breedingCostUnitTypeId: number;
    // gnRHCostPerDose: number;
    // GnRHProductTypeId: number;
    // pgCostPerDose: number;
    // pgProductTypeId: number;
    // professionalServiceCostPerCow: number;
    // femaleCalfPrice: number;
    // cIDRCostPerDose: number;
    // feedCostDryPerPound: number;
    // feedCostLactatingPerPound: number;
    // useLegacyKpiProcessing: boolean;
    // useLegacyFertilityProcessing: boolean;
    // latestCowfileSizeJson: string;
    latestCowEventDate: string;
    longitude: number;
    latitude: number;
}

/**
 * Farm interface
 * @property {number} farmId - The Id for the farm.
 * @property {string} name - The name of the farm.
 * @property {string} description - A description of the farm.
 * @property {string} addressLine1 - The first line of the farm's address.
 * @property {string} addressLine2 - The second line of the farm's address.
 * @property {string} city - The city where the farm is located.
 * @property {string} state - The state where the farm is located.
 * @property {string} country - The country where the farm is located.
 * @property {string} postalCode - The postal code for the farm's location.
 * @property {number} farmTypeId - The type of the farm. (e.g., free stall)
 * @property {number} breedNameId - The breed name associated with the farm.
 * @property {boolean} allowFertilityAudit - Flag indicating if fertility audit is allowed.
 * @property {boolean} allowTraining - Flag indicating if training is allowed.
 * @property {boolean} isTestFarm - Flag indicating if the farm is a test farm.
 * @property {number} territoryManagerId - The Id of the territory manager associated with the farm.
 * @property {number} status - The status of the farm. (e.g., 1 = active, 2 = inactive)
 * @property {string} measurementStandard - The measurement standard used by the farm. (e.g., metric, imperial)
 * @property {boolean} allowTrendsAndTargets - Flag indicating if trends and targets are allowed.
 * @property {boolean} allowSeasonalTrends - Flag indicating if seasonal trends are allowed.
 * @property {boolean} allowFarmGoals - Flag indicating if farm goals are allowed.
 * @property {boolean} allowEMM - Flag indicating if EMM (Electronic Milk Meter) is allowed.
 * @property {boolean} emmSetupComplete - Flag indicating if EMM setup is complete.
 * @property {string} allowedBetasJson - JSON string of allowed betas for the farm.
 * @property {string} farmGoal - The farm goal of the farm.
 * @property {string} priorFarmGoalsJson - JSON string of prior farm goals.
 * @property {string} changeLogJson - JSON string of the change log for the farm.
 * @property {boolean} isExtinct - Flag indicating if the farm is extinct.
 * @property {number} rawDataDateTimeFormatTypeId - The Id of the date time format type for raw data.
 */
export interface Farm {
    farmId: number;
    name: string;
    description: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    country: string;    
    postalCode: string;
    farmTypeId: number;
    breedNameId: number;
    allowFertilityAudit: boolean;
    allowTraining: boolean;
    isTestFarm: boolean;
    territoryManagerId: number;
    status: number;
    measurementStandard: string;
    allowTrendsAndTargets: boolean;
    allowSeasonalTrends: boolean;
    allowFarmGoals: boolean;
    allowEMM: boolean;
    emmSetupComplete: boolean;
    allowedBetasJson: string;
    farmGoal: string;
    priorFarmGoalsJson: string;
    changeLogJson: string;
    isExtinct: boolean;
    rawDataDateTimeFormatTypeId: number;
}

/**
 * Admin User interface
 * @property {number} personId - The integer identifier for the admin user.
 * @property {string} personKey - The unique identifier of the admin user.
 * @property {string} firstName - The first name of the admin user.
 * @property {string} lastName - The last name of the admin user.
 * @property {string} email - The email address of the admin user.
 * @property {string} address1 - The first line of the admin user's address.
 * @property {string} address2 - The second line of the admin user's address.
 * @property {string} city - The city where the admin user is located.
 * @property {string} state - The state where the admin user is located.
 * @property {string} postCode - The postal code for the admin user's location.
 * @property {string} country - The country where the admin user is located.
 * @property {string} phone - The phone number of the admin user.
 * @property {number} personTypeId - The type of the admin user.
 * @property {number} status - The status of the admin user (eg, 1 - active, 2 - inactive).
 * @property {boolean} isDemoUser - Flag indicating if the admin user is a demo user.
 * @property {string} territory - The territory associated with the admin user.
 **/
export interface AdminUser {
    personId: number;
    personKey: string;
    firstName: string;
    lastName: string;
    email: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    postCode: string;
    country: string;
    phone: string;
    personTypeId: number;
    status: number;
    isDemoUser: boolean;
    territory: string;
}

/**
 * User interface
 * @property {number} personId - The integer identifier for the user.
 * @property {string} personKey - The unique identifier of the user.
 * @property {string} firstName - The first name of the user.
 * @property {string} lastName - The last name of the user.
 * @property {string} email - The email address of the user.
 * @property {string} address1 - The first line of the user's address.
 * @property {string} address2 - The second line of the user's address.
 * @property {string} city - The city where the user is located.
 * @property {string} state - The state where the user is located.
 * @property {string} postCode - The postal code for the user's location.
 * @property {string} country - The country where the user is located.
 * @property {string} phone - The phone number of the user.
 * @property {number} personTypeId - The type of the user.
 * @property {number} status - The status of the user (eg, 1 - active, 2 - inactive).
 * @property {boolean} isDemoUser - Flag indicating if the user is a demo user.
 **/
export interface User {
    personId: number;
    personKey: string;
    firstName: string;
    lastName: string;
    email: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    postCode: string;
    country: string;
    phone: string;
    personTypeId: number;
    status: number;
    isDemoUser: boolean;
}

/**
 * Person Role interface
 * @property {number} farmId - the id of the farm a user is assigned to.
 * @property {number} personId - The integer identifier for the person (user).
 * @property {number} roleId - The integer identifier for the role assigned to the user.
 * @property {number} personType - the person type of the user (eg, 6 - other)
 * @property {string} roleName - The name of the role assigned to the user.
 * @property {number} status - The status of the role assignment (eg, 1 - active, 2 - inactive).
 */
export interface PersonRole {
    farmId: number;
    personId: number;
    roleId: number;
    personType: number;
    roleName: string;
    status: number;
}

/**
 *  Farm Type interface (SQL record)
 * @property {number} id - The unique identifier for the farm type.
 * @property {string} code - The code lookup of the farm type.
 * @property {string} name - The name of the farm type.
 * @property {string} description - The description of the farm type.
 */
export interface FarmType {
    id: number;
    code: string;
    name: string;
    description: string;
}

/**
 * Breed Name interface (SQL record)
 * @property {number} breedNameid - The unique identifier for the breed name.
 * @property {string} code - The code lookup of the breed name.
 * @property {string} name - The name of the breed.
 * @property {string} description - The description of the breed name.
 * @property {number} dropDownSeq - The sequence number for dropdown ordering.
 */
export interface BreedName {
    breedNameid: number;
    code: string;
    name: string;
    description: string;
    dropDownSeq: number;
}

/**
 * CodeFile Item interface (SQL record)
 * @property {number} id - The unique identifier for the code file item.
 * @property {string} codeType - The type of the code file record.
 * @property {string} code - The code lookup of the code file record.
 * @property {any} codeValue - The value of the code file record.
 * @property {number} dropDownSeq - The sequence number for dropdown ordering.
 * @property {string} detailsJson - The details JSON associated with the code file record.
 */
export interface CodeFileItem {
    id: number;
    codeType: string;
    code: string;
    codeValue: any;
    dropDownSeq: number;
    detailsJson: string;
}

/** Group Type Member interface (SQL record)
 * @property {number} id - The unique identifier for the group type member.
 * @property {string} groupTypeCode - The code of the group type.
 * @property {any} groupMemberCode - The code of the group member.
 * @property {string} groupMember - The name of the group member.
 * @property {number} sequence - The sequence number for ordering.
 */
export interface GroupTypeMember{
    id: number;
    groupTypeCode: string;
    groupMemberCode: any;
    groupMember: string;
    sequence: number;
}


/**
 * Parnell Enumeration interface
 * @property {any} value - The value representation of the enumeration.
 * @property {string} name - The name of the enumeration.
 * @property {string} text - The text and often display text of the enumeration.
 */
export interface ParnellEnum {
    value: any;
    name: string;
    text: string;
}

/**
 * StatesAndProvinces interface
 * @property {string} country - The country code.
 * @property {CodeFileItem[]} states - An array of state or province code file items.
 */
export interface StatesAndProvinceItem {
    country: string;
    states: CodeFileItem[];
}

/**
 * Tab interface
 * @property {string} title - The title of the tab.
 * @property {string} name - The name of the tab.
 * @property {boolean} selected - Flag indicating if the tab is currently selected.
 */
export interface Tab {
    title: string;
    name: string;
    selected: boolean;
}

/**
 *  HerdFertilityCalendar Calendar interface
 * @property {number} id - The unique identifier for the calendar.
 * @property {number} herdId - The int id of the herd the calendar belongs to.
 * @property {string} name - The name of the calendar.
 * @property {string} lactationGroup - The the lactation group of the cows the calendar applies to.
 * @property {Date} startDate - The start date of the calendar.
 * @property {Date} startDateString - The start date of the calendar as a string.
 * @property {Date|null} endDate - The end date of the calendar.
 * @property {Date} endDateString - The end date of the calendar as a string.
 * @property {number} startDOP - The start day of phase (DOP) for the calendar.
 * @property {string} enrollmentFrequencyCode - The code representing the enrollment frequency for the calendar.
 * @property {string} enrollmentFrequency - The display text for the enrollment frequency.
 * @property {boolean} isArchived - Flag indicating if the calendar is archived.
 * @property {Date|null} archivedDate - The date the calendar was archived.
 * @property {Date} archivedDateString - The archived date of the calendar as a string.
 * @property {FertilityCalendarItem[]} fertilityCalendarItems - An array of fertility calendar items associated with the calendar.
 * @property {boolean} isTemplate - Flag indicating if the calendar is a template.
 */
export interface HerdFertilityCalendar {
    id?: number;
    herdId: number;
    name: string;
    lactationGroup: string;
    startDate: Date;
    startDateString?: string;
    endDate?: Date;
    endDateString?: string;
    startDOP: number;
    enrollmentFrequencyCode: string;
    enrollmentFrequency?: string;
    isArchived: boolean;
    archivedDate: Date | null;
    archivedDateString?: string;
    fertilityCalendarItems?: FertilityCalendarItem[];
    isTemplate: boolean;
}


/**
 *  Fertility Calendar Item interface
 * @property {number} id - The unique identifier for the fertility calendar item.
 * @property {number} herdId - The identifier of the herd this fertility calendar item belongs to.
 * @property {number} herdFertilityCalendarItemId - The unique identifier for the fertility calendar item within the context of the herd fertility calendar.
 * @property {number} herdFertilityCalendarId - The identifier of the fertility calendar this item belongs to.
 * @property {number} herdFertilityCalendarItemListId - The identifier of the fertility calendar list item associated with this calendar item.
 * @property {string} class - The style class for the procedure.
 * @property {string} text - The short text representation of the procedure.
 * @property {string} description - The detailed description of the procedure.
 * @property {number} [dayOfWeek] - (Optional) The day of the week for the procedure (1 = Sunday, 7 = Saturday).
 * @property {'AM' | 'PM'} [timeOfDay] - (Optional) The time of day for the procedure ('AM' or 'PM').
 * @property {number} [weekNumber] - (Optional) The week number for the procedure, used for grouping in the calendar view.
 * @property {number} dOP - The day of phase (DOP) for the procedure, used for positioning in the calendar view.
 * @property {string} dOPType - The type of the DOP (e.g., "DOA", "DIM", "DSLH"), use in the label of the day cell the procedure is on.
 */
export interface FertilityCalendarItem {
    id: number;
    herdId: number;
    herdFertilityCalendarItemId?:number | null;
    herdFertilityCalendarId: number;
    herdFertilityCalendarItemListId?: number;
    class: string;
    text: string;
    description: string;
    dayOfWeek?: number;
    timeOfDay?: 'AM' | 'PM';
    dOP: number;
    dOPType:string;
    weekNumber?: number;
    dayCellIndex?: number;
}

/**
 * Fertility Calendar List Item interface
 * @property {number} id - The unique identifier for the calendar list item.
 * @property {string} itemType - The type of the calendar list item (e.g., 'standard', 'custom', etc.).
 * @property {string} classCode - The css class code for the calendar list item.
 * @property {string} text - The text to be displayed for the calendar list item.
 * @property {string} description - The description of the calendar list item.
 * @property {number} sequence - The sequence number for ordering the calendar list items in the UI.
 */
export interface FertilityCalendarListItem {
    id: number;
    itemType: string;
    classCode: string;
    class?: string;
    text: string;
    description: string;
    sequence: number;
}
/**
 * Fertility Calendar Enrollment Frequency interface
 * @property {number} id - The unique identifier for the enrollment frequency.
 * @property {string} code - The code representing the enrollment frequency.
 * @property {string} codeValue - The value associated with the enrollment frequency code.
 * @property {number} rangeFactor - The range factor associated with the enrollment frequency, used for calculating DOP range labels.
 * @property {number} sequence - The sequence number for ordering the enrollment frequency options in the UI.
 */

export interface FertilityCalendarEnrollmentFrequency{
    id: number;
    code: string;
    codeValue: string;
    rangeFactor: number;
    sequence: number;
}

